/**
 * Iter 185 — Site-wide Header / Footer canvas chrome.
 * ============================================================================
 *
 * PREVIOUS IMPLEMENTATION (iter 172-ish): This file hosted CanvasHeader +
 * CanvasFooter that rendered `<PreviewHeader />` / `<PreviewFooter />` and
 * saved via `/api/builder/theme`. It supported nav links + layout preset +
 * sticky toggle + copyright text, but was missing everything the client
 * asked for in iter 185: logo-image upload, logo-position control, menu
 * styles, header BUTTONS (search / shop_now variants), reorder controls,
 * footer link columns, footer social links, footer policy links.
 *
 * ITER 185 REWRITE: Replaced with `<SiteHeader />` + `<SiteFooter />`
 * (see `site-chrome/SiteChrome.js`) backed by the new
 * `/api/builder/site/chrome` endpoint (see `marketplace/site_chrome_routes.py`).
 * The public site page uses the exact same components so the editor
 * preview is 1:1 with what the customer sees.
 *
 * Editing model: hover the header/footer → "Edit" pill appears →
 * click it to open the inspector side-drawer with all controls. Inline
 * logo-text editing also works while the drawer is open.
 *
 * Public API of this module is unchanged: `CanvasHeader({ theme, site,
 * onSaved })` + `CanvasFooter({...})` so DashboardContent.js doesn't need
 * to change.
 */
import React, { useState } from 'react';
import { Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { useSiteChrome, SiteHeader, SiteFooter } from '../site-chrome/SiteChrome';
import { HeaderInspector, FooterInspector } from '../site-chrome/SiteChromeInspectors';
import { StableInlineE } from '../content-editor/DashboardParts';


/* ─────────────────────────────────────────────────────────────
 *  <CanvasHeader />
 * ─────────────────────────────────────────────────────────── */
export function CanvasHeader({ theme, site, onSaved }) {
  const { header, save, loading } = useSiteChrome();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  if (loading || !header) {
    return <div className="h-16 bg-gray-100" data-testid="canvas-header-loading" />;
  }

  const current = draft || header;

  const openEditor = () => { setDraft(header); setEditing(true); };
  const cancelEdit = () => { setDraft(null); setEditing(false); };
  const commit = async () => {
    try {
      await save({ header: draft });
      toast.success('Header saved');
      onSaved && onSaved({ site_header: draft });
      setDraft(null);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save header');
    }
  };

  // Inline logo text edit — flows through to the draft (unsaved) until commit.
  const onLogoTextChange = (val) => setDraft({ ...(draft || header), logo_text: val });

  return (
    <div className="relative group/header" data-testid="canvas-header-wrapper">
      <SiteHeader
        header={current}
        editable
        onEdit={openEditor}
        E={StableInlineE}
        onLogoTextChange={onLogoTextChange}
      />
      {!editing && (
        <button
          onClick={openEditor}
          className="absolute top-3 right-3 z-20 opacity-0 group-hover/header:opacity-100 px-3 py-1.5 text-[10px] tracking-[0.18em] font-bold uppercase text-white bg-black/70 backdrop-blur-md rounded hover:bg-[#A78BFA] flex items-center gap-1.5 shadow-lg"
          data-testid="canvas-header-edit-btn"
        >
          <Pencil size={11} /> Edit Header
        </button>
      )}
      {editing && (
        <ChromeDrawer
          title="Edit Header"
          onClose={cancelEdit}
          onSave={commit}
          testid="canvas-header-drawer"
        >
          <HeaderInspector value={draft || header} onChange={setDraft} />
        </ChromeDrawer>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
 *  <CanvasFooter />
 * ─────────────────────────────────────────────────────────── */
export function CanvasFooter({ theme, site, onSaved }) {
  const { footer, save, loading } = useSiteChrome();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  if (loading || !footer) {
    return <div className="h-24 bg-gray-100" data-testid="canvas-footer-loading" />;
  }

  const current = draft || footer;

  const openEditor = () => { setDraft(footer); setEditing(true); };
  const cancelEdit = () => { setDraft(null); setEditing(false); };
  const commit = async () => {
    try {
      await save({ footer: draft });
      toast.success('Footer saved');
      onSaved && onSaved({ site_footer: draft });
      setDraft(null);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save footer');
    }
  };

  const onLogoTextChange = (val) => setDraft({ ...(draft || footer), logo_text: val });

  return (
    <div className="relative group/footer" data-testid="canvas-footer-wrapper">
      <SiteFooter
        footer={current}
        editable
        onEdit={openEditor}
        E={StableInlineE}
        onLogoTextChange={onLogoTextChange}
      />
      {!editing && (
        <button
          onClick={openEditor}
          className="absolute top-3 right-3 z-20 opacity-0 group-hover/footer:opacity-100 px-3 py-1.5 text-[10px] tracking-[0.18em] font-bold uppercase text-white bg-black/70 backdrop-blur-md rounded hover:bg-[#A78BFA] flex items-center gap-1.5 shadow-lg"
          data-testid="canvas-footer-edit-btn"
        >
          <Pencil size={11} /> Edit Footer
        </button>
      )}
      {editing && (
        <ChromeDrawer
          title="Edit Footer"
          onClose={cancelEdit}
          onSave={commit}
          testid="canvas-footer-drawer"
        >
          <FooterInspector value={draft || footer} onChange={setDraft} />
        </ChromeDrawer>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
 *  Shared side-drawer shell
 *  Module-level component (iter 183 stable-identity pattern) so
 *  the inspector subtree never remounts on save.
 * ─────────────────────────────────────────────────────────── */
function ChromeDrawer({ title, onClose, onSave, children, testid }) {
  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-md flex" onClick={onClose} data-testid={testid}>
      <div
        className="ml-auto h-full w-[440px] bg-[#15152E] border-l border-white/10 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-white/[0.08] flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#9CA3AF]">Site Chrome</p>
            <h3 className="text-white font-bold text-[15px] mt-0.5" style={{ fontFamily: 'Syne, sans-serif' }}>{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-[#9CA3AF] hover:text-white p-1"
            data-testid={`${testid}-close`}
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {children}
        </div>

        <div className="px-5 py-3 border-t border-white/[0.08] flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[11px] tracking-[0.18em] uppercase text-[#9CA3AF] hover:text-white font-bold"
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-5 py-2 text-[11px] tracking-[0.18em] uppercase font-bold text-[#0E0E23] bg-[#A78BFA] hover:bg-white rounded transition-all"
            data-testid={`${testid}-save`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
